import { AxiosRequestConfigMethods } from "@/api/types";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import { EnvironmentVariables } from "@/modules/common/types";
import {
  AddParticipantBody,
  AgentTask,
  BoardSummary,
  ByParticipantResponse,
  CasesListResponse,
  CaseWorkflowView,
  CloneStepPreview,
  CompleteStepBody,
  CompoundCheckResult,
  AppealThreadsResponse,
  CreateStepClonesBody,
  EscalationThread,
  FinancePreviewData,
  KanbanCasesPage,
  NoteAttachmentContext,
  NoteAttachmentView,
  OptionSourceRegistry,
  ParticipantBoardResponse,
  ParticipantRef,
  RunFunctionResponse,
  SaveSubmissionBody,
  SendPaymentPlanResponse,
  StartWorkflowCaseBody,
  StepCloneOutcome,
  StepDetail,
  StepHistoryResponse,
  StepHistoryFilter,
  StepUpload,
  UploadDocumentResponse,
} from "../dtos";
import { wmBlobError, wmRequest, wmRequestRaw, wmUrl } from "./wm-client";

/**
 * THIS lead's workflow case(s): list by crmJourneyUid — the canonical CRM ↔ case link.
 * Both creation paths persist it (CRM-initiated forwards the lead's journey uid; a
 * WM-initiated case stores the uid returned by link-or-create-nawy-now), so the tab
 * resolves either origin without relying on externalRef.
 */
export const getCasesByCrmJourneyUid = async (
  crmJourneyUid: string
): Promise<CasesListResponse> =>
  wmRequest<CasesListResponse>({
    url: wmUrl("/v1/cases"),
    method: "GET",
    params: { crmJourneyUid, page: 1, pageSize: 20 },
  });

/**
 * §NawyNowKanban: the board's stage columns — the phases of the Nawy Now flow, in authored order.
 *
 * `lineOfBusiness` is what keeps this to a board's worth of columns. Without it the endpoint answers with
 * every phase name any case in the tenant has ever instantiated, which spans the legacy loan, unified and
 * deal-flow templates — around twenty columns, most of them another product's phases, in whatever order the
 * distinct query returned. Scoped to the LOB it returns that LOB's DEFAULT template's phases: the same strip
 * the lead page's Nawy Now tab draws, and the flow every CRM-started application actually runs.
 */
export const getCaseStages = async (): Promise<string[]> =>
  wmRequest<string[]>({
    url: wmUrl("/v1/cases/stages"),
    method: "GET",
    params: { lineOfBusiness: "NAWY_NOW" },
  });

/**
 * §NawyNowKanban: one stage column's page of the case board.
 *
 * §TeamScope: the board shows the caller and their team, not the tenant. Workflow-manager resolves that from
 * the BEARER TOKEN through the replicated sales hierarchy — there is no uid to pass, and none to spoof.
 * `agentUid` and `supervisorUid` narrow inside it; the server intersects both with the visible set, so neither
 * can reach outside the caller's own team.
 *
 * `scope: "own-team"` is now INERT — workflow-manager scopes every caller from the token and ignores the
 * parameter (it stays on the DTO only because `forbidNonWhitelisted` would 400 clients still sending it).
 * Still sent deliberately: if that server change were ever reverted, the old code path reads its absence as
 * "the whole tenant", and this board must not be the thing that opens.
 */
export const getKanbanCases = async (params: {
  stage: string;
  /**
   * §MultiAgentFilter: narrow to SEVERAL assigned sales agents. Sent comma-separated (see below) because
   * workflow-manager parses the parameter off the query string, exactly as it does `caseUids`.
   *
   * Can only narrow: the server intersects it with the caller's own visible set, so naming agents outside
   * your scope yields nothing rather than their board.
   */
  agentUids?: string[];
  /**
   * §SupervisorFilter: a whole TEAM rather than one agent — this supervisor plus everyone who reports to them,
   * resolved server-side from the replicated hierarchy (the same rule the CRM leads table's own Supervisor
   * filter uses, so the two surfaces agree about who is on whose team). It can only narrow: workflow-manager
   * intersects it with the caller's scope.
   */
  supervisorUid?: string;
  page: number;
  pageSize: number;
  /** §BoardChips: the active chips, as server-side filters — same predicates the counts are built from. */
  slaBreached?: boolean;
  stalledDays?: number;
  /** §BoardSearch: matches the PRIMARY applicant's phone (workflow-manager's `phone`, a contains match). */
  phone?: string;
  /**
   * §NeedsAction: only these cases. The one chip with no case predicate behind it — "needs action" means a
   * step in the CALLER's queue, which workflow-manager serves as a queue, not as a property of a case. The
   * board reads its own queue and names the cases, so the column returns exactly the rows the chip counted
   * instead of whatever the browser had paged in.
   */
  caseUids?: string[];
  /** §BoardSearch: matches the PRIMARY applicant's name (workflow-manager's `customerName`). */
  customerName?: string;
}): Promise<KanbanCasesPage> =>
  wmRequest<KanbanCasesPage>({
    url: wmUrl("/v1/cases"),
    method: "GET",
    // §NeedsAction: the DTO parses `caseUids` from a comma-separated string, so join it here rather than
    // letting the client serialize a repeated `caseUids[]=` param the server does not read.
    // §Status: the board shows LIVE work. A cancelled case keeps its `currentStage`, so without this it sat in
    // a stage column looking active (21 such cases on testing); a completed one moves to the 'Completed' stage,
    // which is not a column, so those already dropped out. Sent from here rather than defaulted server-side so
    // no other consumer of GET /v1/cases changes behaviour.
    params: {
      status: "ACTIVE",
      lineOfBusiness: "NAWY_NOW",
      ...params,
      caseUids: params.caseUids?.length ? params.caseUids.join(",") : undefined,
      // §MultiAgentFilter: joined here for the same reason as caseUids — the DTO parses a comma-separated
      // string, and letting axios serialize a repeated `agentUids[]=` would silently filter by nobody.
      agentUids: params.agentUids?.length ? params.agentUids.join(",") : undefined,
      scope: "own-team",
    },
  });

/**
 * §BoardChips: the header's counts over the whole scoped population.
 *
 * `stalledDays` is sent rather than assumed because the chip is LABELLED with it ("Inactive 10d+") — passing
 * it keeps the number on screen and the number counted the same value by construction. Scope mirrors the
 * columns (the caller's own team, plus the assigned-user and supervisor filters) so the header describes the
 * board underneath it.
 */
export const getBoardSummary = async (params: {
  /** §MultiAgentFilter: mirrors the columns, so the header counts the population it sits above. */
  agentUids?: string[];
  /** §SupervisorFilter: mirrors the columns, so the header counts the population it sits above. */
  supervisorUid?: string;
  stalledDays: number;
}): Promise<BoardSummary> =>
  wmRequest<BoardSummary>({
    url: wmUrl("/v1/cases/board-summary"),
    method: "GET",
    params: {
      lineOfBusiness: "NAWY_NOW",
      ...params,
      agentUids: params.agentUids?.length ? params.agentUids.join(",") : undefined,
      scope: "own-team",
    },
  });

/**
 * Trigger a new flow instance for the lead — via crm-backend (NOT straight to workflow-manager):
 * the backend sources the customer (name + default phone/email) from the CRM record and forwards
 * to workflow-manager, so the created case always carries its PRIMARY participant.
 */
export const startWorkflowCase = async (
  customerUid: string,
  body: StartWorkflowCaseBody
): Promise<{ data: CaseWorkflowView }> =>
  fetchFromBrandedUrl({
    options: {
      brandedUrl: brandUrl(
        `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/${customerUid}/workflow-cases`
      ),
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: body,
    },
  });

/**
 * §Clone-steps: the SOURCE application's cloneable steps that hold data — used by the "Start
 * another application" picker, which has no target case yet (the real target checks run at the
 * creation seam once the agent actually confirms). `caseUid` here is the SOURCE application.
 */
export const getCloneSourcePreview = async (
  sourceCaseUid: string
): Promise<CloneStepPreview[]> =>
  wmRequest<CloneStepPreview[]>({
    url: wmUrl(`/v1/cases/${sourceCaseUid}/step-clones/source-preview`),
    method: "GET",
  });

/**
 * §Clone-steps: preview copying `sourceCaseUid`'s step data onto an EXISTING application. `caseUid`
 * here is the TARGET application — the one "Copy data from another application" was opened from.
 */
export const getCloneTargetPreview = async (
  targetCaseUid: string,
  sourceCaseUid: string
): Promise<CloneStepPreview[]> =>
  wmRequest<CloneStepPreview[]>({
    url: wmUrl(`/v1/cases/${targetCaseUid}/step-clones/preview`),
    method: "GET",
    params: { sourceCaseUid },
  });

/**
 * §Clone-steps: copy steps into an EXISTING application (starting a NEW one with a clone goes
 * through `startWorkflowCase`'s `cloneFrom` instead — WM plans it inside the same creation
 * transaction). A 409 `CLONE_OVERWRITE_REQUIRED` means at least one requested step already has
 * target data — resend with `overwrite: true` once the agent confirms.
 */
export const createStepClones = async (
  targetCaseUid: string,
  body: CreateStepClonesBody
): Promise<StepCloneOutcome[]> =>
  wmRequest<StepCloneOutcome[]>({
    url: wmUrl(`/v1/cases/${targetCaseUid}/step-clones`),
    method: "POST",
    data: body,
  });

/**
 * §AgentTasks: MY open WM to-do list — every AGENT-flagged step on the cases assigned to me as the
 * CRM agent. Workflow-manager resolves who "me" is from the bearer token, so there is no parameter
 * to get wrong (or to spoof).
 */
export const getAgentQueue = async (): Promise<AgentTask[]> =>
  wmRequest<AgentTask[]>({
    url: wmUrl("/v1/tasks/agent-queue"),
    method: "GET",
  });

/** Every workflow-manager case where this identity (phone) is an applicant — each row is a full board. */
export const getCasesByParticipant = async ({
  phone,
  clientUid,
}: {
  phone?: string;
  clientUid?: string;
}): Promise<ByParticipantResponse> =>
  wmRequest<ByParticipantResponse>({
    url: wmUrl("/v1/cases/by-participant"),
    method: "GET",
    params: { phone, clientUid },
  });

/** Loose phone equality: compare the last digits (≥7) so +20 / 0-prefix / spacing variants match. */
const phoneMatches = (a?: string | null, b?: string | null): boolean => {
  const x = (a ?? "").replace(/\D/g, "");
  const y = (b ?? "").replace(/\D/g, "");
  const n = Math.min(x.length, y.length, 9);
  return n >= 7 && x.slice(-n) === y.slice(-n);
};

/**
 * Persona board, composed from the two endpoints workflow-manager actually serves today:
 * GET :uid/participants (pick by loose phone match → PRIMARY fallback) + GET
 * :uid/participants/:participantUid/steps (the participant-scoped view: party-filtered
 * steps annotated with `access`, view steps schema-stripped). When workflow-manager ships
 * a one-call GET :uid/board?phones= with the same server-side pick, collapse this back
 * into a single wmRequest — the return contract is already shaped for it.
 */
/** The case's applicants (PRIMARY + joint/guarantor co-applicants), with §PhoneLinking status. */
export const getCaseParticipants = async (
  caseUid: string
): Promise<ParticipantRef[]> =>
  wmRequest<ParticipantRef[]>({
    url: wmUrl(`/v1/cases/${caseUid}/participants`),
    method: "GET",
  });

/** Add a joint client / guarantor by mobile — creates a claimable placeholder that
 * auto-links to the platform app user on signup (§PhoneLinking). */
export const addCaseParticipant = async (
  caseUid: string,
  body: AddParticipantBody
): Promise<unknown> =>
  wmRequest<unknown>({
    url: wmUrl(`/v1/cases/${caseUid}/participants`),
    method: "POST",
    data: body,
  });

/** §Widgets: Send-to-Credit readiness preview (server-side completeness of SAVED data). */
export const getFinancePreview = async (
  caseUid: string
): Promise<FinancePreviewData> =>
  wmRequest<FinancePreviewData>({
    url: wmUrl(`/v1/cases/${caseUid}/finance-preview`),
    method: "GET",
  });

/**
 * §OptionSources: the tenant's option-source registry — every config list a `select` can bind to
 * through `optionsFrom` (developers, compounds, property types, evaluators, …). Tenant config, so
 * the hook caches it hard; a step schema only names the source, never the values.
 */
export const getOptionSources = async (): Promise<OptionSourceRegistry> =>
  wmRequest<OptionSourceRegistry>({
    url: wmUrl("/v1/cases/option-sources"),
    method: "GET",
  });

/**
 * §Widgets (compoundCheck): look up the selected compound's approval status. Writes the
 * `compoundRouting` fact + the checked-input stamps the step's submit gate reads, so the agent
 * cannot complete Compound Data until this has run against the CURRENT selection.
 */
export const checkCompound = async (
  caseUid: string
): Promise<CompoundCheckResult> =>
  wmRequest<CompoundCheckResult>({
    url: wmUrl(`/v1/cases/${caseUid}/compound/check`),
    method: "POST",
  });

/**
 * §Widgets (stepHistory): the step's normalized audit timeline. Paged — the widget loads the
 * first page lazily on expand, so a board full of history placements costs nothing until opened.
 */
export const getStepHistory = async (
  stepUid: string,
  params?: StepHistoryFilter & { cursor?: string; limit?: number }
): Promise<StepHistoryResponse> =>
  wmRequest<StepHistoryResponse>({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/history`),
    method: "GET",
    params: {
      limit: params?.limit ?? 20,
      ...(params?.cursor ? {cursor: params.cursor} : {}),
      ...(params?.scope ? {scope: params.scope} : {}),
      ...(params?.fieldKey ? {fieldKey: params.fieldKey} : {}),
      ...(params?.sourceStepOrder !== undefined ? {sourceStepOrder: params.sourceStepOrder} : {}),
    },
  });

/**
 * §Widgets: persist the computed payment plan and notify the customer (server recomputes).
 * The step uid rides along because the server authorizes the send against the PRESSING step's
 * placement (`widget.sendToCustomer` + `widget.attachment`) — without it the call is refused.
 */
export const sendPaymentPlan = async (
  caseUid: string,
  stepUid?: string
): Promise<SendPaymentPlanResponse> =>
  wmRequest<SendPaymentPlanResponse>({
    url: wmUrl(`/v1/cases/${caseUid}/payment-plan/send`),
    method: "POST",
    data: stepUid ? { stepUid } : undefined,
  });

/**
 * §Functions: run a registered flow function on the case in MANUAL mode — the generic
 * widget-button path (workflow-manager's own step screen calls this for "Generate plan file").
 * `skipped` is a normal outcome, not an error: the function declined (e.g. no cash price yet),
 * and the caller reports the reason rather than claiming a file was produced.
 */
export const runCaseFunction = async (
  caseUid: string,
  key: string
): Promise<RunFunctionResponse> =>
  wmRequest<RunFunctionResponse>({
    url: wmUrl(`/v1/cases/${caseUid}/functions/${key}/run`),
    method: "POST",
  });

export const getWorkflowBoard = async (
  caseUid: string,
  phones?: string
): Promise<ParticipantBoardResponse> => {
  const participants = await getCaseParticipants(caseUid);
  const phoneList = (phones ?? "").split(",").filter(Boolean);
  const participant =
    participants.find((p) =>
      phoneList.some((phone) => phoneMatches(phone, p.customer?.phone))
    ) ??
    participants.find((p) => p.type === "PRIMARY") ??
    participants[0];
  if (!participant) {
    throw new Error("This case has no participants to build a board for.");
  }
  const view = await wmRequest<CaseWorkflowView>({
    url: wmUrl(`/v1/cases/${caseUid}/participants/${participant.uid}/steps`),
    method: "GET",
  });
  return { ...view, participant };
};

export const getStepDetail = async (stepUid: string): Promise<StepDetail> =>
  wmRequest<StepDetail>({
    url: wmUrl(`/v1/tasks/steps/${stepUid}`),
    method: "GET",
  });

export const saveStepSubmission = async (
  stepUid: string,
  body: SaveSubmissionBody
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/submission`),
    method: "POST",
    data: body,
  });

/** §A3: take a NON-terminal action — fires its authored effects (e.g. Change Unit's time-travel)
 * WITHOUT completing the step. Terminal actions go through completeStep instead. */
export const invokeStepAction = async (
  stepUid: string,
  actionKey: string,
  comment?: string,
  attachmentUids?: string[]
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/invoke-action`),
    method: "POST",
    data: {
      actionKey,
      ...(comment ? { comment } : {}),
      ...(attachmentUids?.length ? { attachmentUids } : {}),
    },
  });

/**
 * Reconcile a telephone with the CRM customer BEFORE the workflow submission is saved:
 * crm-backend passes when it's already linked, adds it when unknown, and 409s when it
 * belongs to another customer — the submission itself then goes browser → workflow-manager
 * directly, like every other save.
 */
export const ensureCustomerTelephone = async (
  customerUid: string,
  telephone: string
): Promise<unknown> =>
  fetchFromBrandedUrl({
    options: {
      brandedUrl: brandUrl(
        `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/${customerUid}/customer-telephones/ensure`
      ),
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { telephone },
    },
  });

/**
 * §Escalate-thread: the escalation conversation on a step, WITHOUT the step-view gate.
 *
 * Deliberately not part of the step detail. `GET /v1/tasks/steps/:uid` 403s an agent on a step owned by
 * another team — a Credit Review step is the normal case — which is precisely the step whose escalation
 * they most need to read. This endpoint answers for them, bounded by what it returns rather than by who
 * asks: escalation roots and their replies, nothing else.
 */
export const getEscalationThread = async (
  stepUid: string
): Promise<EscalationThread> =>
  wmRequest<EscalationThread>({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/escalation-thread`),
    method: "GET",
  });

/**
 * §Comments (product ask, 2026-08-06): the WM per-step comment thread, postable from the CRM.
 *
 * §Escalate-overdue: `parentUid` turns the post into a reply on that comment's thread. Omitted for an
 * ordinary note — the body must not carry the key at all, since the endpoint runs `forbidNonWhitelisted`
 * and an explicit `undefined` would be dropped by JSON.stringify anyway but reads as if it were sent.
 */
export const addStepComment = async (
  stepUid: string,
  content: string,
  parentUid?: string,
  attachmentUids?: string[]
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/comments`),
    method: "POST",
    data: {
      ...(content ? { content } : {}),
      ...(parentUid ? { parentUid } : {}),
      ...(attachmentUids?.length ? { attachmentUids } : {}),
    },
  });

/**
 * §Review-appeal: the appeal threads visible on this step — the ones the agent raised here, and (for a
 * reviewer) the ones raised against a decision made here. Each carries its own `canReply` and
 * `isCurrentDecision`, both server-resolved: the client cannot derive either.
 */
export const getAppealThreads = async (
  stepUid: string
): Promise<AppealThreadsResponse> =>
  wmRequest<AppealThreadsResponse>({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/appeals`),
    method: "GET",
  });

/**
 * §Review-appeal: challenge a standing rejected/conditional credit decision.
 *
 * `uploadUid` is required for a document and identifies the exact row — several uploads can share one
 * placement key (a multi-file placement, a per-participant document, the two faces of a pair), so the key
 * alone would let the server anchor the appeal to a different file and a different reviewer.
 */
export const openStepAppeal = async (
  stepUid: string,
  body: {
    kind: "document" | "field" | "value";
    fieldKey: string;
    uploadUid?: string;
    content: string;
    attachmentUids?: string[];
  }
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/appeals`),
    method: "POST",
    data: {
      kind: body.kind,
      fieldKey: body.fieldKey,
      ...(body.uploadUid ? { uploadUid: body.uploadUid } : {}),
      content: body.content,
      ...(body.attachmentUids?.length ? { attachmentUids: body.attachmentUids } : {}),
    },
  });

/**
 * §Review-appeal: answer on an appeal thread. Keyed by the APPEAL's uid rather than a step, because the two
 * parties stand on different steps — the agent on the capture step, the reviewer on the step they decided on.
 */
export const replyToAppeal = async (
  appealUid: string,
  content: string,
  attachmentUids?: string[]
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(`/v1/tasks/appeals/${appealUid}/replies`),
    method: "POST",
    data: {
      ...(content ? { content } : {}),
      ...(attachmentUids?.length ? { attachmentUids } : {}),
    },
  });

/**
 * §Escalate-overdue: raise an overdue step to the escalation contacts configured for it.
 *
 * The note is required by the CRM (the modal will not confirm without one) rather than by the endpoint —
 * an escalation with no reason gives the manager receiving it nothing to act on. The server decides who
 * may call this: the case's own agent on an OVERDUE step, an escalation-group member, or a super admin.
 * Body is exactly `{content}` — the endpoint runs `forbidNonWhitelisted`.
 */
export const escalateStep = async (
  stepUid: string,
  content: string,
  attachmentUids?: string[]
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/escalate`),
    method: "POST",
    data: {
      content,
      ...(attachmentUids?.length ? { attachmentUids } : {}),
    },
  });

/**
 * §Terminate: flag the application TERMINATED — WM halts the flow and hides the case from every CRM
 * surface. Requires the `can-terminate-nawy-now-flow` UMS permission (WM enforces it; the button is
 * gated on the same passport entry). The server 409s a non-active or already-terminated case.
 */
export const terminateWorkflowCase = async (
  caseUid: string,
  reason?: string,
  attachmentUids?: string[]
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(`/v1/cases/${caseUid}/terminate`),
    method: "POST",
    data: {
      ...(reason ? { reason } : {}),
      ...(attachmentUids?.length ? { attachmentUids } : {}),
    },
  });

export const completeStep = async (
  stepUid: string,
  body: CompleteStepBody
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/complete`),
    method: "POST",
    data: body,
  });

/** §paymentProof: the answers a `paymentProof` placement requires alongside the file. Instapay needs
 *  the mobile number the transfer came from; a bank transfer needs the transaction reference. */
export interface PaymentProofAnswers {
  method?: string;
  mobileNumber?: string;
  transactionReferenceId?: string;
}

/** Upload against a document global-field placement (runs OCR when configured). */
export const uploadStepDocument = async (
  stepUid: string,
  fieldKey: string,
  file: File,
  /** §TypedDocs: required by multi-type placements (registry `fileTypes`) — the engine rejects an
   * untyped upload into one with "requires a document type". */
  docType?: string,
  /** §Replace: the exact live upload this file supersedes (omit when adding another file). */
  replacesUploadUid?: string,
  /** §Two-sided: which face of whose card — REQUIRED side on a two-sided document; an omitted
   * pairUid starts a NEW owner's pair ("Add another owner"). */
  pairSlot?: { side: "front" | "back"; pairUid?: string },
  /** §paymentProof: the payment answers this placement demands alongside the file. */
  payment?: PaymentProofAnswers,
  /** §Late-docs-note: the optional note sales attached to a late addition — WM stores it as a
   * document-anchored note beside the file (and refuses it on a document that takes no additions). */
  comment?: string,
  attachmentUids?: string[]
): Promise<UploadDocumentResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("fieldKey", fieldKey);
  if (docType) formData.append("docType", docType);
  if (replacesUploadUid) formData.append("replacesUploadUid", replacesUploadUid);
  if (pairSlot?.side) formData.append("side", pairSlot.side);
  if (pairSlot?.pairUid) formData.append("pairUid", pairSlot.pairUid);
  // §paymentProof: a placement flagged `paymentProof` refuses a file that arrives without the
  // payment answers — the engine 422s with PAYMENT_METHOD_INVALID / PAYMENT_REFERENCE_REQUIRED.
  // The CRM never sent them, so uploading an appraisal fee proof was impossible from here.
  if (payment?.method) formData.append("paymentMethod", payment.method);
  if (payment?.mobileNumber) formData.append("mobileNumber", payment.mobileNumber);
  if (payment?.transactionReferenceId)
    formData.append("transactionReferenceId", payment.transactionReferenceId);
  if (comment) formData.append("comment", comment);
  if (attachmentUids?.length) formData.append("attachmentUids", JSON.stringify(attachmentUids));
  return wmRequest<UploadDocumentResponse>({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/documents`),
    method: "POST",
    data: formData,
    formData: true,
  });
};

export interface NoteAttachmentDownload {
  url: string | null;
  fileName: string;
  mimeType: string;
}

export const stageNoteAttachment = async (
  caseUid: string,
  file: File,
  context: NoteAttachmentContext,
  signal?: AbortSignal
): Promise<NoteAttachmentView> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("context", JSON.stringify(context));
  return wmRequest<NoteAttachmentView>({
    url: wmUrl(`/v1/cases/${caseUid}/note-attachments`),
    method: "POST",
    data: formData,
    formData: true,
    signal,
  });
};

export const removeStagedNoteAttachment = async (
  caseUid: string,
  attachmentUid: string
): Promise<void> =>
  wmRequest<void>({
    url: wmUrl(`/v1/cases/${caseUid}/note-attachments/${attachmentUid}`),
    method: "DELETE",
  });

export const getNoteAttachmentDownload = async (
  caseUid: string,
  attachmentUid: string
): Promise<NoteAttachmentDownload> =>
  wmRequest<NoteAttachmentDownload>({
    url: wmUrl(`/v1/cases/${caseUid}/note-attachments/${attachmentUid}/download-url`),
    method: "GET",
  });

export const readNoteAttachment = async (
  caseUid: string,
  attachmentUid: string
): Promise<Blob> =>
  wmRequest<Blob>({
    url: wmUrl(`/v1/cases/${caseUid}/note-attachments/${attachmentUid}/download`),
    method: "GET",
    responseType: "blob",
  });

/** Plain (non document-field) file upload on a step. */
export const uploadStepFile = async (
  stepUid: string,
  file: File,
  fieldKey?: string
): Promise<StepUpload> => {
  const formData = new FormData();
  formData.append("file", file);
  if (fieldKey) formData.append("fieldKey", fieldKey);
  return wmRequest<StepUpload>({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/uploads`),
    method: "POST",
    data: formData,
    formData: true,
  });
};

/** §OCR review: correct one extracted value on a document (re-opens the confirm state server-side). */
export const editExtractionField = async (
  stepUid: string,
  docKey: string,
  ocrFieldKey: string,
  value: unknown,
  /** scope the edit to one file's snapshot on a multi-file placement */
  uploadUid?: string,
  /** §Two-sided: which OWNER's pair to correct — the edit lands on BOTH of that pair's rows and
   * un-confirms it. Required once the document holds more than one owner. */
  pairUid?: string
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(
      `/v1/tasks/steps/${stepUid}/documents/${encodeURIComponent(docKey)}/extraction/field`
    ),
    method: "PATCH",
    data: {
      ocrFieldKey,
      value,
      ...(uploadUid ? { uploadUid } : {}),
      ...(pairUid ? { pairUid } : {}),
    },
  });

/** §Doc-request: record which extra documents this placement needs. FROM_CLIENT also surfaces
 * them in the customer's app and notifies them; ON_BEHALF keeps the upload internal. An empty
 * items list clears the request. */
export const saveDocumentRequest = async (
  stepUid: string,
  body: {
    fieldKey: string;
    mode: "ON_BEHALF" | "FROM_CLIENT";
    items: { category: string; type: string }[];
    attachmentUids?: string[];
  }
): Promise<{ notified: boolean }> =>
  wmRequest({
    url: wmUrl(`/v1/tasks/steps/${stepUid}/document-requests`),
    method: "POST",
    data: {
      fieldKey: body.fieldKey,
      mode: body.mode,
      items: body.items,
      ...(body.attachmentUids?.length ? { attachmentUids: body.attachmentUids } : {}),
    },
  });

/** §Two-sided: confirm ONE owner's pair. Per owner, never per document — confirming one owner must
 * not authorise the AML check or step completion for another. */
export const confirmDocumentPair = async (
  stepUid: string,
  docKey: string,
  pairUid: string
): Promise<{ pairUid: string; changed: boolean }> =>
  wmRequest({
    url: wmUrl(
      `/v1/tasks/steps/${stepUid}/documents/${encodeURIComponent(docKey)}/pairs/${pairUid}/confirm`
    ),
    method: "POST",
  });

/** §Two-sided: file a LEGACY side-less upload as a front or back ("Use as front / Use as back").
 * Such a file blocks step completion until it is assigned or deleted. */
export const assignUploadSide = async (
  stepUid: string,
  docKey: string,
  uploadUid: string,
  side: "front" | "back",
  /** the owner pair to join; omitted starts a NEW owner's pair */
  pairUid?: string
): Promise<{ uploadUid: string; side: string; pairUid: string }> =>
  wmRequest({
    url: wmUrl(
      `/v1/tasks/steps/${stepUid}/documents/${encodeURIComponent(
        docKey
      )}/uploads/${uploadUid}/side`
    ),
    method: "PATCH",
    data: { side, ...(pairUid ? { pairUid } : {}) },
  });

/** §OCR review: confirm a document's extracted values are correct (flips `confirmed`). */
export const confirmExtraction = async (
  stepUid: string,
  docKey: string
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(
      `/v1/tasks/steps/${stepUid}/documents/${encodeURIComponent(docKey)}/extraction/confirm`
    ),
    method: "POST",
  });

/** §Manual-AML: screen one uploaded file against the blacklist (uses the file's CURRENT subject). */
export const screenUploadAml = async (
  stepUid: string,
  docKey: string,
  uploadUid: string
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(
      `/v1/tasks/steps/${stepUid}/documents/${encodeURIComponent(
        docKey
      )}/uploads/${uploadUid}/aml-screen`
    ),
    method: "POST",
  });

export const deleteStepDocumentUpload = async (
  stepUid: string,
  docKey: string,
  uploadUid: string
): Promise<unknown> =>
  wmRequest({
    url: wmUrl(
      `/v1/tasks/steps/${stepUid}/documents/${encodeURIComponent(
        docKey
      )}/uploads/${uploadUid}`
    ),
    method: "DELETE",
  });

/** Render the saved form for signing. WM retains the editable source and returns PDF here. */
export const generateStepDocument = async (
  stepUid: string,
  docKey: string,
  fallbackName: string
): Promise<string> => {
  const response = await wmRequestRaw<Blob>({
    url: wmUrl(
      `/v1/tasks/steps/${stepUid}/documents/${encodeURIComponent(docKey)}/generate`
    ),
    method: "POST",
    params: {purpose: "signing"},
    responseType: "blob",
  }).catch(async (error) => {
    throw await wmBlobError(error);
  });
  const disposition = String(response.headers?.["content-disposition"] ?? "");
  const encoded = /filename="?([^"]+)"?/.exec(disposition)?.[1];
  const fileName = encoded ? decodeURIComponent(encoded) : fallbackName.replace(/\.docx?$/i, ".pdf");
  const objectUrl = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
  return fileName;
};

/**
 * Download an upload. workflow-manager 302-redirects to a presigned URL; window.open
 * can't carry the Authorization header, so fetch as a blob (the browser follows the
 * redirect transparently) and trigger a local save.
 */
const pendingDownloads = new Map<string, Promise<void>>();

export const downloadUpload = (uploadUid: string, fileName: string): Promise<void> => {
  const pending = pendingDownloads.get(uploadUid);
  if (pending) return pending;
  const download = downloadSigningUpload(uploadUid, fileName).finally(() => pendingDownloads.delete(uploadUid));
  pendingDownloads.set(uploadUid, download);
  return download;
};

const downloadSigningUpload = async (uploadUid: string, fallbackName: string): Promise<void> => {
  const metadata = await wmRequest<{fileName: string; mimeType: string}>({
    url: wmUrl(`/v1/tasks/uploads/${uploadUid}/download-url`),
    method: "GET",
    params: {purpose: "signing"},
  });
  const blob = await wmRequest<Blob>({
    url: wmUrl(`/v1/tasks/uploads/${uploadUid}/download`),
    method: "GET",
    params: {purpose: "signing"},
    responseType: "blob",
  }).catch(async error => {throw await wmBlobError(error);});
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = metadata.fileName || fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};
